import {FC, ReactNode, useEffect, useState} from 'react';
import {toast} from 'react-toastify';
import {orgStorage} from '@/utils/services/storage.service';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getErrorMessage} from '@/utils/helpers';
import {useNavigate} from 'react-router-dom';
import {ROUTER_URL, STORAGE_KEY} from '@/utils/enums';
import {getOrganizationInfo, getOrganizationSelector, getTeamMemberInfo, getTeamSelector} from '@/store/organization';
import {IOrganization, ITeam} from '@/utils/types';
import {
	organizationGetNotificationSettings,
	organizationUpdateNotificationSettings,
} from '@/store/organization-notificationSetting';
import {organizationNotificationSettingsInitialData} from '@/utils/constants/notificationSettings';

interface OrganizationPrivateRouteProps {
	children: ReactNode;
}

const OrganizationPrivateRoute: FC<OrganizationPrivateRouteProps> = ({children}) => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const organization: IOrganization | null = useAppSelector(getOrganizationSelector);
	const team: ITeam | null = useAppSelector(getTeamSelector);

	const organizationId = orgStorage.get(STORAGE_KEY.ORGANIZATION_ID);
	const teamId = orgStorage.get(STORAGE_KEY.TEAM_MEMBER_ID);

	const organizationRole = orgStorage.get(STORAGE_KEY.ORGANIZATION_ROLE);

	const [isLoading, setIsLoading] = useState(true);

	const getOrganization = async () => {
		try {
			await dispatch(getOrganizationInfo(organizationId as number)).unwrap();
		} catch (error) {
			toast.error(getErrorMessage(error));
		}
	};

	const getTeamMember = async () => {
		try {
			await dispatch(getTeamMemberInfo(teamId as number)).unwrap();
		} catch (error) {
			toast.error(getErrorMessage(error));
		}
	};

	useEffect(() => {
		if (!organizationId) navigate(`${ROUTER_URL.MAIN}${ROUTER_URL.LOGIN}`);

		if (organizationRole === 'Owner' && !organization && organizationId) getOrganization();

		if ((organizationRole === 'Admin' || organizationRole === 'Standart') && !team && teamId) getTeamMember();

		setIsLoading(false);
	}, []);

	useEffect(() => {
		if (!organizationId) return;
		const getSettings = async () => {
			try {
				await dispatch(organizationGetNotificationSettings(organizationId as number)).unwrap();
			} catch (error) {
				if (error.status === 400) {
					try {
						await dispatch(
							organizationUpdateNotificationSettings({
								id: organizationId as number,
								reqBody: organizationNotificationSettingsInitialData,
							})
						).unwrap();
					} catch (error) {
						toast.error(getErrorMessage(error));
					}
				}
			}
		};

		getSettings();
	}, [organization]);

	return !isLoading && children;
};

export default OrganizationPrivateRoute;
